import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useUnknownChargeContext } from '../../providers/UnknownChargeProvider';
import { PageLayout } from '../PageLayout/PageLayout';
import {
  makeDateFormatter,
  usePremiumCurrencyFormatter,
} from '@wix/premium-react-text';
import { DetailsList } from './DetailsList';
import { Consolidated } from './Consolidated';
import { useAppContext } from '../../providers/AppContextProvider';
import { paymentMethodDisplay } from '@wix/billing-client-common';
import { Box, Button, Text, TextButton } from '@wix/design-system';
import { useNavigate } from 'react-router';
import s from './ChargeDetails.scss';
import { PremiumAssetType } from '@wix/ambassador-premium-billing-and-payments/types';
import { useBiLogger } from '../../providers/BiLoggerProvider';
import { LOGIN_TO_BILLING_HISTORY_URL } from '../../constants';
import { useResultsPagePhase1 } from '../../hooks/useResultsPagePhase1';

export const ChargeDetails: React.FC = () => {
  const {
    appContext: { locale },
  } = useAppContext();
  const {
    chargeDetails: {
      amount,
      description,
      currencyCode,
      isConsolidated,
      date,
      service,
      paymentMethod: { label, type },
    },
  } = useUnknownChargeContext();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { biLogger } = useBiLogger();
  const phase1Enabled = useResultsPagePhase1();
  const [formatCurrency] = usePremiumCurrencyFormatter({ currencyCode });
  const { format: dateFormatter } = makeDateFormatter(locale, 'shortMonth');
  const { shortName, identifier } = paymentMethodDisplay(
    { label: type, paymentBrand: label },
    locale
  );

  const searchAgainClick = () => {
    biLogger.searchAgainClick();
    navigate('/');
  };

  const subtitle = isConsolidated
    ? 'charge-locator.results-page.consolidated-invoices.subtitle'
    : service === PremiumAssetType.PLAN
    ? 'charge-locator.results-page.plan.subtitle'
    : 'charge-locator.results-page.subtitle';

  return (
    <PageLayout
      title={t('charge-locator.results-page.title')}
      subtitle={t(subtitle, {
        amount: formatCurrency(amount),
        productType: description,
        eventDate: dateFormatter(date),
        paymentMethod: shortName,
        cardNumber: identifier,
      })}
    >
      <Box className={s.wrapper} direction="vertical" align="left">
        {isConsolidated ? <Consolidated /> : <DetailsList />}
        {phase1Enabled && (
          <Box marginTop="SP4">
            <Button
              dataHook="charge-details__login-cta"
              as="a"
              href={LOGIN_TO_BILLING_HISTORY_URL}
              onClick={() => biLogger.loginToAccountClick()}
            >
              {t('charge-locator.results-page.login-cta', 'Log in to your account')}
            </Button>
          </Box>
        )}
        <Box marginTop="SP3">
          <Text>
            <Trans i18nKey="charge-locator.results-page.find-another-charge">
              charge-locator.results-page.find-another-charge
              <TextButton
                dataHook="charge-details__search-again"
                as="a"
                onClick={searchAgainClick}
              >
                charge
              </TextButton>
            </Trans>
          </Text>
        </Box>
      </Box>
    </PageLayout>
  );
};
